<script lang="ts" setup>
// Core
import { computed } from "vue";

// Types
import { ButtonPropsT } from "@/@types/components";

// Components
import Popover from "./PopoverComponent.vue";
import Icon from "@/components/core/IconCore.vue";

const buttonProps = defineProps<ButtonPropsT>();

const actionHandler = () => {
  if (buttonProps.action && typeof buttonProps.action === "function") {
    buttonProps.action();
  }
};

const eventHandlers = computed(() => {
  const handlers: Record<string, () => void | undefined> = {
    click: actionHandler
  };

  if (buttonProps.preventDefault) {
    handlers.click = (event?: Event) => {
      event?.preventDefault();
      event?.stopPropagation();
      actionHandler();
    };
  }

  return handlers;
});
</script>

<template>
  <Popover
    v-bind="popover"
    arrow
    :disabled="!tooltip && !$slots.tooltip_children"
    :content="tooltip"
    :teleported="disableTeleportedTooltip ? false : true"
    :full-width="fullWidth"
  >
    <template #reference>
      <button
        :id="id"
        :class="[
          'main-button',
          btnClass,
          btnStyle || 'filled',
          btnStyle === 'gradient' && `gradient--${btnGradientColor}`,
          size || 'large',
          disabledStyle && 'disabledStyle',
          status,
          {
            'main-button__icon-right': iconRight,
            'main-button--full-width': fullWidth,
            'main-button--no-hover': disableHover,
            'main-button--uppercase': uppercase,
            'main-button--bold-font': boldFont,
            'main-button--pulse': pulse,
            'main-button--prevent-icon-color': preventIconColor,
            responsive: !preventResponsive
          }
        ]"
        :type="type"
        :disabled="disabled || loading"
        :aria-label="ariaLabel"
        v-on="eventHandlers"
      >
        <template v-if="!loading">
          <Icon
            v-if="btnIcon"
            :icon-src="btnIcon"
            :icon-size="iconSize"
            :icon-class="noSvgStyle ? 'no-svg-style' : iconClass"
          />
          <span v-if="content" class="main-button__content">
            {{ content || "" }}
          </span>
          <slot v-if="$slots.children" name="children" />
        </template>
        <template v-else>
          <span class="spinner-border spinner-border-sm align-middle" />
          <template v-if="loadingLabel">{{ loadingLabel }}</template>
        </template>
      </button>
    </template>
    <template v-if="$slots.tooltip_children" #children>
      <slot name="tooltip_children" />
    </template>
  </Popover>
</template>

<style lang="scss" scoped>
@include pulse($primary_4);

@mixin hover-style($iconColor, $bgColor, $txtColor, $boxShadow: null) {
  &:hover:not(:disabled, .main-button--no-hover, .disabledStyle) {
    @include define-icons-style($iconColor);
    background: $bgColor;
    color: $txtColor;
    border-color: $bgColor;

    &.main-button--prevent-icon-color {
      :deep(path) {
        @include svg-path-styles($iconColor);
      }
    }

    @if ($boxShadow) {
      box-shadow: $boxShadow;
    }
  }
}

@mixin active-style($iconColor, $bgColor, $txtColor, $boxShadow: null) {
  &:active:not(:disabled, .main-button--no-hover, .disabledStyle) {
    @include define-icons-style($iconColor);
    background: $bgColor;
    color: $txtColor;
    border-color: $bgColor;

    &.main-button--prevent-icon-color {
      :deep(path) {
        @include svg-path-styles($iconColor);
      }
    }

    @if ($boxShadow) {
      box-shadow: $boxShadow;
    }
  }
}

.main-button {
  @include reset-button();
  @include flex-center();
  border-radius: $sp_button;
  gap: $spacing_ultra_xs;
  padding: $md_button_padding;
  font-size: $fs_sm;
  font-weight: $fw_emd;
  transition: $transition_duration_fast;

  .lottie-animation-container {
    @include square-size(40px);
  }

  &__icon-right {
    flex-direction: row-reverse;
  }

  &--prevent-icon-color {
    &.filled,
    &.gradient {
      :deep(path) {
        @include svg-path-styles($primary_1);
      }
    }

    &.outlined,
    &.no-border,
    &.active {
      :deep(path) {
        @include svg-path-styles($new-primary_7);
      }
    }

    &.success,
    &.danger {
      :deep(path) {
        @include svg-path-styles($neutral_1);
      }
    }
  }

  &--uppercase {
    text-transform: uppercase;
  }

  &--full-width {
    width: 100%;
  }

  &--pulse {
    animation: pulse 1s infinite;
  }

  &--bold-font {
    font-weight: $fw_bd;
  }

  &.extra-small {
    max-height: 28px;
    padding: $spacing_ultra_xs $spacing_sm;
    font-size: $fs_ssm;
    font-weight: $fw_emd;

    :deep(svg) {
      max-width: 18px;
    }
  }

  &.small {
    font-size: $fs_ssm;
  }

  &.medium {
    max-height: $button_md_max_size;
  }

  &.large {
    padding: $lg_button_padding;
  }

  &.border {
    @include hover-style($new-neutral_0, $new-primary_10, $new-neutral_0, $btn_box_shadow);
    @include define-icons-style($new-primary_7);
    background: $new-neutral_0;
    color: $new-neutral_16;
    border: 1px solid $new-neutral_3 !important;

    &:hover {
      border: 1px solid $primary_5;
    }
  }

  &.filled,
  &.gradient {
    @include define-icons-style($primary_1);
    @include hover-style($new-neutral_0, $new-primary_10, $new-neutral_0, $btn_box_shadow);
    @include active-style($btn_font_active, $btn_fill_active, $btn_font_active);
    background: $new-primary_7;
    color: $new-neutral_0;
  }

  &.gradient {
    @include gradient-bg-custom("primary");

    &.gradient--primary_2 {
      @include gradient-bg-custom("primary_2");
    }

    &.gradient--success {
      @include gradient-bg-custom("green");
    }

    &.gradient--magenta {
      @include gradient-bg-custom("magenta");
    }
  }

  &.outlined {
    @include define-icons-style($new-primary_7);
    @include hover-style($new-neutral_0, $new-primary_10, $new-neutral_0, $btn_box_shadow);
    @include active-style($btn_font_active, $btn_fill_active, $btn_font_active);
    color: $new-primary_7;
    border: 1px solid $new-primary_7;

    &:disabled,
    &.disabledStyle {
      @include define-icons-style($neutral_7);
      background: transparent;
      color: $neutral_7;
      border: 1px solid $neutral_7;
    }
  }

  &.no-border,
  &.active {
    @include define-icons-style($new-primary_7);
    @include active-style($primary_5, transparent, $primary_5);
    @include hover-style($new-primary_7, $new-primary_1, $new-primary_7);
    color: $new-primary_7;
    border: none;

    &:disabled,
    &.disabledStyle {
      background: transparent;
      color: $new-neutral_10;
      border: 0;
    }
  }

  &.secondary {
    color: $new-neutral_9;
    border: none;

    .main-button__content {
      text-decoration: underline;
    }
  }

  &.tertiary {
    @include define-icons-style($new-neutral_0);
    @include hover-style($new-neutral_0, $tertiary_7, $new-neutral_0);
    @include active-style($new-neutral_0, $tertiary_6, $new-neutral_0);
    background: $tertiary_8;
    color: $new-neutral_0;
    border: 1px solid $tertiary_8;

    &.outlined {
      @include define-icons-style($tertiary_8);
      color: $tertiary_8;
      background: transparent;
    }
  }

  &.active {
    @include hover-style($primary_5, $neutral_3, $primary_5);
    background: $primary_1;
  }

  &.success {
    @include define-icons-style($neutral_1);
    @include hover-style($neutral_1, $green_6, $neutral_1, $btn_box_shadow);
    @include active-style($neutral_1, $green_7, $neutral_1);
    background: $green_7;
    color: $neutral_1;
    border: 1px solid $green_7;

    &.outlined {
      color: $green_8;
      background: transparent;
      @include define-icons-style($green_7);
    }

    &:disabled,
    &.disabledStyle {
      @include define-icons-style($neutral_7);
    }
  }

  &.danger {
    @include define-icons-style($neutral_1);
    @include hover-style($neutral_1, $red_6, $neutral_1, $btn_box_shadow);
    @include active-style($neutral_1, $red_5, $neutral_1);
    background: $red_7;
    color: $neutral_1;
    border: 1px solid $red_7;

    &.outlined {
      @include define-icons-style($red_7);
      color: $red_7;
      background: transparent;
    }

    &:disabled,
    &.disabledStyle {
      @include define-icons-style($new-neutral_7);
      border: 1px solid $new-neutral_7;
      color: $new-neutral_7;
    }
  }

  &.warning {
    @include define-icons-style($neutral_1);
    @include hover-style($neutral_1, $orange_6, $neutral_1, $btn_box_shadow);
    @include active-style($neutral_1, $orange_5, $neutral_1);
    background: $orange_8;
    color: $neutral_1;
    border: 1px solid $orange_8;

    &.outlined {
      @include define-icons-style($orange_8);
      color: $orange_8;
      background: transparent;
    }

    &:disabled,
    &.disabledStyle {
      @include define-icons-style($neutral_7);
    }
  }

  &:disabled,
  &.disabledStyle {
    @include define-icons-style($new-neutral_10);
    background: $new-neutral_3;
    color: $new-neutral_10;
    border: 1px solid $new-neutral_3;

    :deep(svg) path:not(.circle-path) {
      @include svg-path-styles($new-neutral_10);
    }
  }

  &.disabledStyle {
    cursor: default;
  }

  &:disabled {
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid black !important;
  }

  .spinner-border {
    @include square-size($icon_size_md);
  }

  @media (max-width: $mobile_lg) {
    &.responsive {
      max-height: $mobile_button_lg_max_height;
      padding: $mobile_lg_button_padding !important;
    }
  }
}
</style>
